import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TrainOutlinedIcon from '@mui/icons-material/TrainOutlined';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { FunctionComponent } from 'react';

import { Dialog } from '../../../../../components/Dialog';
import { Tooltip } from '../../../../../components/Tooltip';
import { EventUsersDrawer } from '../../../../../user/components/EventUsersDrawer/EventUsersDrawer';
import { CarpoolTripData } from '../../../../graphql/query/carpoolTripsQuery.graphql';
import { useCarpoolTripDrawer } from './useCarpoolTripDrawer';

interface CarpoolTripDrawerProps {
  currentUserId: string | null;
  eventId: string;
  hasPaidTicket: boolean;
  isOpen: boolean;
  isStaff: boolean;
  onClose: () => void;
  trip: CarpoolTripData;
  userJoinedTripId: string | null;
}

const formatUserName = (
  firstname: string | null,
  lastname: string | null,
  nickname?: string | null,
) => {
  const fullName = `${firstname ?? ''} ${lastname ?? ''}`.trim();

  if (!nickname) {
    return fullName || 'Participant';
  }

  return fullName ? `${fullName} (${nickname})` : nickname;
};

export const CarpoolTripDrawer: FunctionComponent<CarpoolTripDrawerProps> = ({
  currentUserId,
  eventId,
  hasPaidTicket,
  isOpen,
  isStaff,
  onClose,
  trip,
  userJoinedTripId,
}) => {
  const {
    canDeleteTrip,
    canJoinTrip,
    excludedUserIds,
    isCurrentUserDriver,
    isCurrentUserPassenger,
    isDeleteDialogOpen,
    isEventUsersDrawerOpen,
    isUserAlreadyInAnotherTrip,
    onAddPassenger,
    onCloseDeleteDialog,
    onCloseEventUsersDrawer,
    onDeleteTrip,
    onJoinTrip,
    onLeaveTrip,
    onOpenEventUsersDrawer,
    onRemovePassenger,
    onShowDeleteDialog,
  } = useCarpoolTripDrawer({
    currentUserId,
    hasPaidTicket,
    isStaff,
    trip,
    userJoinedTripId,
    onClose,
  });

  return (
    <>
      <Drawer anchor="right" open={isOpen} onClose={onClose}>
        <Box
          sx={{ width: { xs: 360, sm: 440 }, maxWidth: '100vw', p: 3, pt: 11 }}
        >
          <Stack spacing={3}>
            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Départ
                  </Typography>
                  <Typography variant="h4">{trip.pickupTown}</Typography>
                </Box>
                <Chip
                  color={trip.remainingSeats > 0 ? 'success' : 'default'}
                  label={`${trip.remainingSeats}/${trip.seats} place${trip.seats > 1 ? 's' : ''} restante${trip.remainingSeats > 1 ? 's' : ''}`}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {format(new Date(trip.pickupAt), "EEEE d MMMM 'à' HH:mm", {
                  locale: fr,
                })}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {isCurrentUserDriver ? (
                <Chip label="Vous conduisez" color="primary" />
              ) : null}
              {isCurrentUserPassenger ? (
                <Chip label="Vous êtes passager" color="success" />
              ) : null}
            </Stack>

            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Conducteur
              </Typography>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="h6">
                    {formatUserName(
                      trip.driver.firstname,
                      trip.driver.lastname,
                      trip.driver.nickname,
                    )}
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <LocationOnOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {trip.driver.studyTown ?? 'Ville non renseignée'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <SchoolOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {trip.driver.studies ?? 'Études non renseignées'}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Stack spacing={1.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Détails du trajet
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <TrainOutlinedIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Lieu précis de départ
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {trip.pickupExactLocation}
                  </Typography>
                </Box>
              </Stack>
              {trip.description ? (
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {trip.description}
                  </Typography>
                </Box>
              ) : null}
            </Stack>

            <Divider />

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Passagers
                </Typography>
                {isStaff ? (
                  <Button
                    size="small"
                    startIcon={<GroupAddIcon />}
                    onClick={onOpenEventUsersDrawer}
                  >
                    Ajouter
                  </Button>
                ) : null}
              </Stack>
              {trip.passengers.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {trip.passengers.map((passenger) => (
                    <ListItem
                      key={passenger.id}
                      disableGutters
                      secondaryAction={
                        isStaff ? (
                          <Tooltip title="Retirer du trajet">
                            <IconButton
                              color="error"
                              onClick={() => onRemovePassenger(passenger.id)}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        ) : undefined
                      }
                    >
                      <ListItemText
                        primary={formatUserName(
                          passenger.firstname,
                          passenger.lastname,
                          passenger.nickname,
                        )}
                        secondary={`${passenger.studyTown ?? 'Ville non renseignée'}${passenger.studies ? ` • ${passenger.studies}` : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucun passager pour le moment.
                </Typography>
              )}
            </Box>

            <Stack spacing={1.5}>
              {canJoinTrip ? (
                <Button variant="contained" onClick={onJoinTrip}>
                  Rejoindre ce trajet
                </Button>
              ) : null}

              {isCurrentUserPassenger ? (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={onLeaveTrip}
                >
                  Quitter ce trajet
                </Button>
              ) : null}

              {!hasPaidTicket ? (
                <Typography variant="body2" color="text.secondary">
                  Le covoiturage sera disponible après le paiement de votre
                  place.
                </Typography>
              ) : null}

              {hasPaidTicket &&
              !isCurrentUserDriver &&
              !isCurrentUserPassenger &&
              isUserAlreadyInAnotherTrip ? (
                <Typography variant="body2" color="text.secondary">
                  Vous êtes déjà inscrit dans un autre trajet pour cet
                  événement.
                </Typography>
              ) : null}

              {hasPaidTicket &&
              !isCurrentUserDriver &&
              !isCurrentUserPassenger &&
              !isUserAlreadyInAnotherTrip &&
              trip.remainingSeats <= 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Ce trajet est complet.
                </Typography>
              ) : null}

              {canDeleteTrip ? (
                <Button color="error" onClick={onShowDeleteDialog}>
                  Supprimer ce trajet
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      <EventUsersDrawer
        eventId={eventId}
        excludeUserIds={excludedUserIds}
        hasPaid
        isOpen={isEventUsersDrawerOpen}
        onClose={onCloseEventUsersDrawer}
        onUserSelected={onAddPassenger}
      />

      <Dialog
        description="Ce trajet sera supprimé pour tous les passagers."
        isDestructive
        onCancel={onCloseDeleteDialog}
        onSubmit={onDeleteTrip}
        open={isDeleteDialogOpen}
        submitText="Supprimer"
        title="Supprimer ce trajet ?"
      />
    </>
  );
};
